import React, { useEffect, useState } from "react";
import Header from "../../../Componets/Header";
import { get_user_features } from "../../../Controllers/FeatureLock";
import FeatureUnlockModal from "../Form";
import toast from "react-hot-toast";
import Loadercomponent from "../../../Componets/Loader";
import { feature_unlock_request_email } from "../../../Controllers/FeatureUnLockMail";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import { encryptAndStoreTimestamp } from "../../../Secret/Feature_lock";
import { useLocation, useNavigate } from 'react-router-dom';

const FeatureDashboard = () => {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [showKeyInput, setShowKeyInput] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkInitialAccess = () => {
      const searchParams = new URLSearchParams(location.search);
      const keyFromUrl = searchParams.get("KEY");

      console.log("Initial URL Key check:", keyFromUrl);

      if (keyFromUrl) {
        verifyKey(keyFromUrl);
      } else {
        setShowKeyInput(true);
      }
    };

    checkInitialAccess();
  }, [location.search]);

  const verifyKey = async (providedKey) => {
    try {
      const storedKey = localStorage.getItem("encryptedTimestamp");

      if (providedKey === storedKey) {
        setAccessGranted(true);
        setShowKeyInput(false);
        fetchFeatures();
        localStorage.removeItem("encryptedTimestamp");
        toast.success("Access granted!");
      } else {
        toast.error("Invalid access key. Please check and try again.");
        localStorage.removeItem("encryptedTimestamp");
        setShowKeyInput(true);
      }
    } catch (error) {
      console.error("Key verification error:", error);
      toast.error("Failed to verify access key");
      localStorage.removeItem("encryptedTimestamp");
      setShowKeyInput(true);
    }
  };

  const handleKeySubmit = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) {
      toast.error("Please enter an access key");
      return;
    }
    verifyKey(keyInput);
  };

  const handleGenerateKey = async () => {
    try {
      await encryptAndStoreTimestamp();
      const generatedKey = localStorage.getItem("encryptedTimestamp");

      navigator.clipboard.writeText(generatedKey).then(() => {
        toast.success("Key generated and copied to clipboard!");
      });

    } catch (error) {
      toast.error("Failed to generate key");
    }
  };

  const fetchFeatures = async () => {
    try {
      setLoader(true);
      const response = await get_user_features();
      if (response?.status) {
        setFeatures(response.data || []);
      } else {
        toast.error("Failed to load features");
      }
    } catch (error) {
      toast.error("Error loading features");
    } finally {
      setLoader(false);
    }
  };

  const handleUnlockClick = (feature) => {
    const lockedBranches = feature.status_per_branch.filter(
      (b) => !b.is_unlocked
    );
    /* setSelectedFeature(feature); */
    setSelectedFeature({
      ...feature,
      lockedBranches
    });
    setIsModalOpen(true);
  };

  const handleUnlockSuccess = () => {
    fetchFeatures();
  };

  const headers = {
    name: "Feature Management",
    components: [],
    navigate: {
      tabs: [
        {
          name: "Dashboard",
          path: "/app/feature-lock",
        },
      ],
    },
  };

  const groupFeaturesByCategory = (features) => {
    const categories = {
      basic: [],
      intermediate: [],
      advanced: [],
      premium: []
    };

    features.forEach(feature => {
      if (feature.standalone_amount <= 100) {
        categories.basic.push(feature);
      } else if (feature.standalone_amount <= 500) {
        categories.intermediate.push(feature);
      } else if (feature.standalone_amount <= 2000) {
        categories.advanced.push(feature);
      } else {
        categories.premium.push(feature);
      }
    });

    return categories;
  };

  const categories = features ? groupFeaturesByCategory(features) : {
    basic: [],
    intermediate: [],
    advanced: [],
    premium: []
  };

  const FeatureCard = ({ feature }) => {
    const allBranchesUnlocked = feature.status_per_branch.every(branch => branch.is_unlocked);

    const isExpired = feature.status_per_branch.some(branch =>
      branch.is_unlocked && branch.expires_at && new Date() > new Date(branch.expires_at)
    );

    return (
      <div className={`border rounded-lg p-4 ${allBranchesUnlocked && !isExpired
        ? 'bg-green-50 border-green-200'
        : 'bg-gray-50 border-gray-200'
        }`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-lg text-gray-800">{feature.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
          </div>
        </div>

        <div className="flex flex-col mt-4 space-y-2">
          {feature.status_per_branch.map(branch => {
            const expired = branch.is_unlocked && branch.expires_at && new Date() > new Date(branch.expires_at);
            return (
              <div key={branch.branch_id} className="flex justify-between items-center text-sm">

                {branch.is_unlocked && (
                  <span>{branch.branch_name}</span>
                )}
                {branch.is_unlocked && (
                  expired ? (
                    <span className="text-red-600">Expired</span>
                  ) : (
                    <span className="text-green-600 font-medium">Unlocked</span>
                  )
                )}
              </div>
            );
          })}
        </div>

        {!allBranchesUnlocked && (
          <button
            onClick={() => handleUnlockClick(feature)}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-500 hover:to-green-800 transition-all duration-200 font-medium"
          >
            Unlock Now
          </button>
        )}
      </div>
    );
  };



  if (showKeyInput) {
    return (
      <Header headers={headers}>
        <div className="p-6">
          <div className="max-w-md mx-auto mt-10">
            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">🔒 Restricted Access</h2>
                <p className="text-gray-600 mt-2">
                  This feature requires a valid access key
                </p>
              </div>

              <form onSubmit={handleKeySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Access Key
                  </label>
                  <input
                    type="text"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Paste your access key here..."
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Verify & Access
                  </button>

                  <button
                    type="button"
                    onClick={handleGenerateKey}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Generate Key
                  </button>
                </div>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.removeItem("encryptedTimestamp");
                      navigate("/app/dashboard");
                    }}
                    className="text-gray-600 hover:text-gray-800 text-sm"
                  >
                    ← Back to Dashboard
                  </button>
                </div>
              </form>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Only Development Team of Masirat Technology can generate access keys.
                  The key is valid for a limited time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Header>
    );
  }

  if (!accessGranted) {
    return (
      <Header headers={headers}>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Verifying access...</p>
            </div>
          </div>
        </div>
      </Header>
    );
  }


  return (
    <Header headers={headers}>
      <Loadercomponent loader={loader}>
        <ScrollWrapper summary={2}>
          <div className="p-6">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Feature Unlock System</h2>
              <p className="text-gray-600">
                This route is not disclosed anywhere in the frontend for customers.
              </p>
            </div>

            {Object.entries(categories).map(([category, categoryFeatures]) => (
              categoryFeatures.length > 0 && (
                <div key={category} className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-700 mb-4 capitalize">
                    {category} Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryFeatures.map((feature) => (
                      <FeatureCard key={feature._id} feature={feature} />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>

          {selectedFeature && (
            <FeatureUnlockModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setSelectedFeature(null);
              }}
              feature={selectedFeature}
              onSuccess={handleUnlockSuccess}
            />
          )}
        </ScrollWrapper>
      </Loadercomponent>
    </Header>
  );
};

export default FeatureDashboard;